import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Phone, MapPin, CheckCircle2, Star, ChevronLeft, ChevronRight, Quote, ArrowRight, ShieldCheck,
  CarFront, Paintbrush, Sparkles, Frame, Truck, KeyRound, FileCheck, ClipboardList, Car,
  Shield, HandHeart, BadgeDollarSign, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BUSINESS, HERO_IMAGE, ABOUT_IMAGE, FEATURES, SERVICES, WHY_CHOOSE, PROCESS_STEPS, GALLERY, TESTIMONIALS } from "../mock";

const iconMap = { CarFront, Paintbrush, Sparkles, Frame, Truck, KeyRound, FileCheck, ClipboardList, Car, Shield, HandHeart, BadgeDollarSign, Clock };

const SectionLabel = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-600 mb-4">
    <span className="w-8 h-[2px] bg-red-600 inline-block" />
    {children}
  </span>
);

const Home = () => {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const featured = TESTIMONIALS.slice(0, 6);

  const next = useCallback(() => setTestimonialIdx((i) => (i + 1) % featured.length), [featured.length]);
  const prev = () => setTestimonialIdx((i) => (i - 1 + featured.length) % featured.length);

  useEffect(() => {
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [next]);

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-red-50 text-red-700 border border-red-100 rounded-full px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6">
              <ShieldCheck size={14} />
              Veteran-Owned & Operated
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-zinc-900 leading-[1.02] mb-6">
              A Level Above <br />
              <span className="text-red-600">The Class.</span>
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-xl mb-8">
              {BUSINESS.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide text-sm h-14 px-8 rounded-md shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200" data-testid="hero-appointment-btn">
                <Link to="/appointment">Make an Appointment <ArrowRight size={17} className="ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-zinc-300 text-zinc-800 hover:border-red-600 hover:text-red-600 hover:bg-white font-semibold uppercase tracking-wide text-sm h-14 px-8 rounded-md transition-all duration-200" data-testid="hero-call-btn">
                <a href={BUSINESS.phoneHref}><Phone size={17} className="mr-1" /> {BUSINESS.phone}</a>
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <MapPin size={15} className="text-red-600" />
              {BUSINESS.address}
            </div>
          </div>

          <div className="relative animate-fade-in">
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-red-600/10 rounded-full blur-2xl" aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img src={HERO_IMAGE} alt="Caliber Auto Repair shop" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <div className="bg-white/95 backdrop-blur rounded-xl px-5 py-3.5 shadow-lg">
                  <div className="flex items-center gap-1 mb-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-xs font-semibold text-zinc-800">5-Star Rated on Google</p>
                </div>
                <div className="bg-red-600 text-white rounded-xl px-5 py-3.5 shadow-lg text-center">
                  <p className="font-heading text-xl font-bold leading-none">Free</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mt-1">Estimates</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature strip */}
        <div className="bg-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-3">
            {["Free Estimates", "In-House Towing", "Collision Repair", "Expert Paint", "Car Drop Facility"].map((f) => (
              <span key={f} className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
                <CheckCircle2 size={16} className="text-red-500" /> {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section className="py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src={ABOUT_IMAGE} alt="Inside the Caliber Auto shop" className="w-full h-[440px] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="absolute -bottom-7 -right-4 sm:right-8 bg-red-600 text-white rounded-2xl px-8 py-6 shadow-2xl">
              <p className="font-heading text-4xl font-extrabold leading-none">100%</p>
              <p className="text-xs font-semibold uppercase tracking-widest mt-1.5">Customer First</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionLabel>About Caliber Auto</SectionLabel>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900 mb-6">
              Welcome To <span className="text-red-600">CaliberAUTO</span>
            </h2>
            <p className="text-zinc-600 leading-relaxed mb-8">
              {BUSINESS.description} As an independent, veteran-owned shop, we answer to you — with best-in-class prices and craftsmanship on every job.
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3.5 mb-9">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2.5 text-sm font-semibold text-zinc-800">
                  <CheckCircle2 size={17} className="text-red-600 shrink-0" /> {f}
                </div>
              ))}
            </div>
            <Button asChild variant="outline" className="border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-semibold uppercase tracking-wide text-xs h-12 px-7 rounded-md transition-all duration-200" data-testid="about-testimonials-link">
              <Link to="/testimonials">Read Our Testimonials <ArrowRight size={15} className="ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="py-24 bg-zinc-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <SectionLabel>Our Services</SectionLabel>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900 mb-5">
              Friendly Specialists, <span className="text-red-600">Reliable Work</span>
            </h2>
            <p className="text-zinc-600 leading-relaxed">
              Auto-repair specialists who take the time to explain the work and give honest, reliable estimates.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <div key={s.id} className="group bg-white rounded-xl p-8 border border-zinc-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300" data-testid={`service-card-${s.id}`}>
                  <div className="w-14 h-14 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-zinc-900 mb-3">{s.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Button asChild className="bg-zinc-900 hover:bg-zinc-800 text-white font-semibold uppercase tracking-wide text-xs h-12 px-8 rounded-md transition-all duration-200" data-testid="view-all-services-btn">
              <Link to="/services">View All Services <ArrowRight size={15} className="ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="py-24 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-600 mb-4 justify-center">
              <span className="w-8 h-[2px] bg-red-600 inline-block" /> Why Choose Us <span className="w-8 h-[2px] bg-red-600 inline-block" />
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900">
              Your Vehicle, <span className="text-red-600">Our Mission</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CHOOSE.map((w) => {
              const Icon = iconMap[w.icon];
              return (
                <div key={w.title} className="text-center p-8 rounded-xl border border-zinc-100 hover:border-red-100 hover:bg-red-50/40 transition-colors duration-300">
                  <div className="w-16 h-16 mx-auto rounded-full bg-zinc-900 text-white flex items-center justify-center mb-5">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-zinc-900 mb-2.5">{w.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{w.description}</p>
                </div>
              );
            })}
          </div>

          {/* Process */}
          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS_STEPS.map((p, i) => (
              <div key={p.step} className="relative pl-5 border-l-2 border-red-600/30">
                <span className="font-heading text-5xl font-extrabold text-red-600/15 absolute -top-3 right-2 select-none">{p.step}</span>
                <h4 className="font-heading text-lg font-bold text-zinc-900 mb-2">{p.title}</h4>
                <p className="text-sm text-zinc-600 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GALLERY PREVIEW ============ */}
      <section className="py-24 bg-zinc-50" data-testid="gallery-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <SectionLabel>Our Work</SectionLabel>
              <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900">
                Inside The <span className="text-red-600">Shop</span>
              </h2>
            </div>
            <Button asChild variant="outline" className="border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-semibold uppercase tracking-wide text-xs h-11 px-6 rounded-md transition-all duration-200 self-start sm:self-auto" data-testid="view-gallery-btn">
              <Link to="/gallery">View Full Gallery <ArrowRight size={15} className="ml-1" /></Link>
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.slice(0, 4).map((g) => (
              <Link key={g.id} to="/gallery" className="group relative rounded-xl overflow-hidden aspect-[4/3] block">
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <p className="absolute bottom-4 left-4 right-4 text-white text-sm font-semibold opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  {g.caption}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-24 bg-zinc-950 relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-red-600/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-500 mb-4 justify-center">
              <span className="w-8 h-[2px] bg-red-500 inline-block" /> Happy Clients <span className="w-8 h-[2px] bg-red-500 inline-block" />
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Our <span className="text-red-500">Testimonials</span>
            </h2>
          </div>

          <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 sm:p-12 shadow-2xl" data-testid="testimonial-card">
            <Quote size={44} className="text-red-600/40 mb-6" />
            <p className="text-zinc-300 leading-relaxed text-base sm:text-lg min-h-[120px]">
              {featured[testimonialIdx].text}
            </p>
            <div className="flex items-center justify-between mt-8">
              <div>
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(featured[testimonialIdx].rating)].map((_, i) => (
                    <Star key={i} size={15} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="font-heading text-white font-bold">{featured[testimonialIdx].name}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-widest mt-0.5">{featured[testimonialIdx].role}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={prev} aria-label="Previous testimonial" data-testid="testimonial-prev" className="w-11 h-11 rounded-full border border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500 flex items-center justify-center transition-colors duration-200">
                  <ChevronLeft size={19} />
                </button>
                <button onClick={next} aria-label="Next testimonial" data-testid="testimonial-next" className="w-11 h-11 rounded-full bg-red-600 text-white hover:bg-red-700 flex items-center justify-center transition-colors duration-200">
                  <ChevronRight size={19} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {featured.map((_, i) => (
              <button
                key={i}
                onClick={() => setTestimonialIdx(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === testimonialIdx ? "w-8 bg-red-500" : "w-2 bg-zinc-700 hover:bg-zinc-600"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-20 bg-red-600 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8 relative">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3">
              Been In An Accident? We've Got You.
            </h2>
            <p className="text-red-100 text-lg">Free estimates, in-house towing, and repairs done right — fast.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Button asChild size="lg" className="bg-white text-red-700 hover:bg-zinc-100 font-bold uppercase tracking-wide text-sm h-14 px-8 rounded-md shadow-lg hover:-translate-y-0.5 transition-all duration-200" data-testid="cta-appointment-btn">
              <Link to="/appointment">Book Now</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/60 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold uppercase tracking-wide text-sm h-14 px-8 rounded-md transition-all duration-200" data-testid="cta-call-btn">
              <a href={BUSINESS.phoneHref}><Phone size={17} className="mr-1.5" /> {BUSINESS.phone}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
